import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jsPDF } from "jspdf";

import useAtendimento from "../../hooks/useAtendimento";
import { useAtendimentoAtual } from "../../hooks/useAtendimentoAtual";
import { useAuth } from "../../auth/AuthContext";

const FichaAtendimento = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { salvarAtendimento, finalizarAtendimento, buscarAtendimentoPorId } = useAtendimento();
  const { atendimentoId: idContexto, limparAtendimento } = useAtendimentoAtual();
  const { user, loading: loadingAuth } = useAuth();

  // Recupera o ID via state da navegação ou pelo contexto
  const atendimentoId = location.state?.atendimentoId || idContexto;

  const [atendimento, setAtendimento] = useState(null);
  const [carregandoDados, setCarregandoDados] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const [assunto, setAssunto] = useState("");
  const [descricao, setDescricao] = useState("");
  const [proximoPasso, setProximoPasso] = useState("");

  // ============================
  // BUSCA DO ATENDIMENTO
  // ============================
  const carregar = useCallback(async () => {
    if (!atendimentoId) return;

    try {
      setCarregandoDados(true);
      const data = await buscarAtendimentoPorId(atendimentoId);
      setAtendimento(data);

      if (data?.assunto) setAssunto(data.assunto);
      if (data?.descricao) setDescricao(data.descricao);
    } catch (error) {
      console.error("Erro ao carregar atendimento:", error);
    } finally {
      setCarregandoDados(false);
    }
  }, [atendimentoId, buscarAtendimentoPorId]);

  useEffect(() => {
    if (!atendimentoId) {
      console.warn("Nenhum ID de atendimento encontrado. Retornando para a fila...");
      navigate("/atendimento/fila-atendimento", { replace: true });
      return;
    }

    carregar();

    const interval = setInterval(carregar, 140000);
    return () => clearInterval(interval);
  }, [atendimentoId, carregar, navigate]);

  // ============================
  // PROTEÇÃO DE ACESSO (PERMISSÕES)
  // ============================
  if (loadingAuth) return <p>Carregando permissões...</p>;
  if (!user) return <p>Não autorizado</p>;

  const perfisPermitidos = ["admin", "advogado", "advogado_parceiro", "estagiario"];
  if (!perfisPermitidos.includes(user.role)) {
    return <p>Acesso negado</p>;
  }

  if (carregandoDados || !atendimento) {
    return <p>Carregando atendimento...</p>;
  }

  const cliente = atendimento.cliente;

  // ============================
  // ENTREVISTAS
  // ============================
  const abrirEntrevista = (rota) => {
    navigate(rota, {
      state: { atendimentoId },
    });
  };

  // ============================
  // SALVAR FICHA NO BANCO DE DADOS
  // ============================
  const salvarFicha = async () => {
    if (!assunto.trim() || !descricao.trim() || !proximoPasso.trim()) {
      alert("Por favor, preencha o assunto, a descrição e o próximo passo antes de salvar.");
      return null;
    }

    try {
      setSalvando(true);

      const resultado = await salvarAtendimento(atendimentoId, {
        assunto,
        descricao,
        advogado: user._id || user.id, // ID obtido via AuthContex
        proximoPasso,
      });

      alert("Ficha de atendimento salva com sucesso!");
      return resultado;
    } catch (error) {
      console.error("Erro ao salvar atendimento:", error);
      alert("Erro ao salvar dados no banco de dados.");
      return null;
    } finally {
      setSalvando(false);
    }
  };

  // ============================
  // FINALIZAR ATENDIMENTO
  // ============================
  const finalizar = async () => {
    try {
      setSalvando(true);

      // Garante o salvamento dos dados de assunto e descrição
      const salvo = await salvarFicha();
      if (!salvo) return;

      await finalizarAtendimento(atendimentoId);

      if (limparAtendimento) {
        limparAtendimento();
      }

      alert("Atendimento finalizado com sucesso!");
      navigate("/atendimento/fila-atendimento", { replace: true });
    } catch (error) {
      console.error("Erro ao finalizar atendimento:", error);
      alert("Erro ao finalizar o atendimento.");
    } finally {
      setSalvando(false);
    }
  };

  // ============================
  // GERAR PDF
  // ============================
  // ============================
  // GERAR PDF COM CABEÇALHO PERSONALIZADO
  // ============================
  const gerarPDF = () => {
    const doc = new jsPDF();

    // ---------------------------------------------------------
    // CABEÇALHO (Estilo TIMBRADO)
    // ---------------------------------------------------------

    // Cor principal do timbre (Vinho / Marrom Avermelhado)
    const corDestaque = [128, 40, 40];

    // 1. Nome Central/Esquerda
    doc.setFont("times", "bold");
    doc.setFontSize(13);
    doc.setTextColor(0, 0, 0);
    doc.text("Rayane Cristina", 135, 20, { align: "right" });

    doc.setFont("times", "normal");
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text("Rodrigues Pessoa Gomes", 135, 25, { align: "right" });

    // 2. Linha Divisória Vertical
    doc.setDrawColor(...corDestaque);
    doc.setLineWidth(0.8);
    doc.line(140, 15, 140, 30);

    // 3. Informações da Advogada / OAB (à direita da linha)
    doc.setFont("times", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...corDestaque);
    doc.text("ADVOGADA", 143, 19);
    doc.text("OAB/PA  27.956", 143, 24);
    doc.text("OAB/MA 23.002-A", 143, 29);

    // 4. Linha Horizontal Inferior do Cabeçalho
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(1.2);
    doc.line(20, 34, 190, 34);

    // ---------------------------------------------------------
    // CORPO DO DOCUMENTO (FICHA DE ATENDIMENTO)
    // ---------------------------------------------------------

    // Título Principal
    doc.setFont("times", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("FICHA DE ATENDIMENTO", 105, 45, { align: "center" });

    // Informações Básicas
    doc.setFontSize(11);
    doc.text(`Cliente: ${cliente?.nomeCliente || "N/A"}`, 20, 57);
    
    doc.setFont("times", "normal");
    doc.text(`Atendido por: ${user.nome || user.email}`, 20, 64);
    doc.text(`Data: ${new Date().toLocaleDateString("pt-BR")}`, 20, 71);

    // Assunto
    doc.setFont("times", "bold");
    doc.text("Assunto:", 20, 83);
    doc.setFont("times", "normal");
    doc.text(assunto || "Não informado", 40, 83);

    // Descrição
    doc.setFont("times", "bold");
    doc.text("Descrição do Atendimento:", 20, 95);
    
    doc.setFont("times", "normal");
    doc.text(descricao || "Sem descrição registrada.", 20, 102, { maxWidth: 170 });

    // Download do Arquivo
    doc.save(`Ficha_${cliente?.nomeCliente || "Atendimento"}.pdf`);
  };
  // ============================
  // UI
  // ============================
  return (
    <div>
      <h2>Ficha de Atendimento</h2>

      <p>
        <b>Cliente:</b> {cliente?.nomeCliente || "Cliente não informado"}
      </p>

      <h3>Entrevistas específicas</h3>

      <div>
        <button onClick={() => abrirEntrevista("/atendimento/trabalhista")}>Trabalhista</button>
        <button onClick={() => abrirEntrevista("/atendimento/previdenciario")}>Previdenciário</button>
        <button onClick={() => abrirEntrevista("/atendimento/civel")}>Cível</button>
        <button onClick={() => abrirEntrevista("/atendimento/criminal")}>Criminal</button>
        <button onClick={() => abrirEntrevista("/atendimento/outros")}>Outros</button>
      </div>

      <br />

      <div>
        <label style={{ display: "block", fontWeight: "bold" }}>Assunto:</label>
        <input
          type="text"
          placeholder="Informe o assunto do atendimento..."
          value={assunto}
          onChange={(e) => setAssunto(e.target.value)}
          style={{ width: "100%", padding: "6px", marginBottom: "10px" }}
        />
      </div>

      <div>
        <label style={{ display: "block", fontWeight: "bold" }}>Descrição:</label>
        <textarea
          placeholder="Descreva o atendimento prestado..."
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          rows={5}
          style={{ width: "100%", padding: "6px" }}
        />
      </div>

      <div style={{ width:"20%" }}>
        <label >Próximo passo:</label>
        <select
          value={proximoPasso}
          onChange={(e) => setProximoPasso(e.target.value)}
          required
        >
          <option>Selecione...</option>
          <option value="peticionar">Peticionar</option>
          <option value="analisar">Analisar</option>
          <option value="recusado">Recusado</option>
        </select>
      </div>

      <br />

      <button className="btn-salvar" onClick={salvarFicha} disabled={salvando}>
        {salvando ? "Salvando..." : "Salvar"}
      </button>

      <button className="btn-salvar" onClick={finalizar} disabled={salvando}>
        Finalizar Atendimento
      </button>

      <button className="btn-salvar" onClick={gerarPDF}>
        Gerar PDF
      </button>

      <button
        className="btn-cancel"
        onClick={() => navigate("/atendimento/fila-atendimento", { replace: true })}
        style={{ backgroundColor: "gray" }}
      >
        Voltar
      </button>
    </div>
  );
};

export default FichaAtendimento;